import { useEffect, useMemo, useRef, useState } from "react";
import {
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryVoronoiContainer,
} from "victory";
import {
  barEmotionOrder,
  bgColors,
  getEmojiImage,
  emotionColors,
} from "../utils";

const CHART_WIDTH = 720;
const CHART_HEIGHT = 330;
const EMOTION_LABEL_CLOSE_THRESHOLD = 2.5;
const EMOTION_LABEL_VERTICAL_SPACING = 12;

const getTooltipHeading = (datum) => datum?.tooltipDateLabel || "";
const buildSortedStudents = (studentCounts = {}) =>
  Object.entries(studentCounts)
    .map(([email, count]) => ({ email, count }))
    .sort((a, b) => b.count - a.count);

const SINGLE_EMOTION_TOOLTIP_WIDTH = 200;
const SINGLE_EMOTION_TOOLTIP_HEIGHT = 200;

const SingleEmotionTooltip = ({
  row,
  heading,
  studentEmailToNameMap,
  tooltipX,
  tooltipY,
}) => {
  const width = SINGLE_EMOTION_TOOLTIP_WIDTH;
  const height = SINGLE_EMOTION_TOOLTIP_HEIGHT;
  const rowBgColor = bgColors[row.emotion] || "#F3F4F6";
  const students = row.students || [];

  return (
    <foreignObject
      x={tooltipX}
      y={tooltipY}
      width={width}
      height={height}
      style={{ pointerEvents: "none" }}
    >
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        className="flex h-full flex-col rounded-lg bg-white border border-gray-200 shadow-lg overflow-hidden"
        style={{ width, height }}
      >
        <div className="px-3 py-1.5">
          <div className="text-[14px] font-bold text-[#111827]">
            {heading}
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1">
          {getEmojiImage(row.emotion) && (
            <img
              src={getEmojiImage(row.emotion)}
              alt={row.emotion}
              className="w-4 h-4"
            />
          )}
          <span
            className="capitalize text-[14px] font-medium"
            style={{ color: row.color }}
          >
            {`${row.emotion}: ${row.percentage}%`}
          </span>
        </div>
        <div className="px-3 pt-2 pb-1 text-[13px] font-semibold text-[#111827]">
          Students who've felt
          <span className="ml-1 capitalize" style={{ color: row.color }}>
            {row.emotion}
          </span>
        </div>
        {students.length > 0 ? (
          <div
            className="min-h-0 flex-1 overflow-y-auto pb-2"
            style={{ pointerEvents: "auto" }}
          >
            {students.map((student, index) => {
              const displayName =
                studentEmailToNameMap?.[student.email] || student.email;
              const isAlt = index % 2 === 0;

              return (
                <div
                  key={student.email}
                  className="flex items-center justify-between px-3 py-1 text-[13px]"
                  style={{
                    backgroundColor: isAlt ? rowBgColor : "transparent",
                  }}
                >
                  <span className="truncate pr-2">{displayName}</span>
                  <span className="text-[#6B7280]">{`${student.count}x`}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-3 py-2 text-[13px] italic text-gray-500">
            No student-specific data available.
          </div>
        )}
      </div>
    </foreignObject>
  );
};

const MULTI_EMOTION_TOOLTIP_WIDTH = 170;

const MultiEmotionTooltip = ({ rows, heading, tooltipX, anchorX }) => {
  return (
    <foreignObject
      x={tooltipX}
      y={8}
      width={MULTI_EMOTION_TOOLTIP_WIDTH}
      height={CHART_HEIGHT - 16}
      style={{ pointerEvents: "none" }}
    >
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        className={`flex w-full ${
          anchorX === "right" ? "justify-end" : "justify-start"
        }`}
      >
        <div className="inline-flex max-w-full flex-col rounded-lg bg-white border border-gray-200 shadow-lg overflow-hidden">
          <div className="px-3 py-1">
            <div className="text-[14px] font-bold whitespace-normal break-words">
              {heading}
            </div>
          </div>
          <div className="flex flex-col overflow-y-auto pb-1.5">
            {rows.map((row) => (
              <div
                key={row.emotion}
                className="flex items-center px-3 text-[14px]"
              >
                <span
                  className="capitalize"
                  style={{ color: row.color }}
                >{`${row.emotion}: ${row.percentage}%`}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </foreignObject>
  );
};

const LineGraphTooltip = ({
  datum,
  selectedEmotions,
  studentEmailToNameMap,
  x,
  y,
}) => {
  const allRows = datum?.tooltipRows || [];
  const selectedEmotionSet = useMemo(
    () => new Set(selectedEmotions),
    [selectedEmotions],
  );
  const rows = useMemo(
    () =>
      allRows
        .filter((row) => selectedEmotionSet.has(row.emotion))
        .sort((a, b) => (b.percentage || 0) - (a.percentage || 0)),
    [allRows, selectedEmotionSet],
  );

  const isSingleEmotion = selectedEmotions.length === 1;
  const chartX = typeof x === "number" ? x : datum._x || 0;
  const chartY = typeof y === "number" ? y : datum._y || 0;
  const width = isSingleEmotion
    ? SINGLE_EMOTION_TOOLTIP_WIDTH
    : MULTI_EMOTION_TOOLTIP_WIDTH;
  const height = isSingleEmotion ? SINGLE_EMOTION_TOOLTIP_HEIGHT : 200;
  const tooltipX = chartX > CHART_WIDTH / 2 ? chartX - width - 12 : chartX + 12;
  const tooltipY = isSingleEmotion ? Math.max(8, chartY - height / 2) : chartY;
  const heading = getTooltipHeading(datum);
  const singleEmotionRow = useMemo(() => {
    if (!isSingleEmotion) return null;

    return {
      ...rows[0],
      students: buildSortedStudents(rows[0]?.studentCounts),
    };
  }, [isSingleEmotion, rows]);

  if (!datum || !rows.length) return null;

  if (isSingleEmotion) {
    return (
      <SingleEmotionTooltip
        row={singleEmotionRow}
        heading={heading}
        studentEmailToNameMap={studentEmailToNameMap}
        tooltipX={tooltipX}
        tooltipY={tooltipY}
      />
    );
  }

  return (
    <MultiEmotionTooltip
      rows={rows}
      heading={heading}
      tooltipX={tooltipX}
      anchorX={chartX > CHART_WIDTH / 2 ? "right" : "left"}
    />
  );
};

const EmotionMarker = ({ x, y, datum, showEmotionName }) => {
  if (typeof x !== "number" || typeof y !== "number") return null;

  if (showEmotionName) {
    return (
      <text
        x={x + 8}
        y={y + 4 + (datum.labelDy || 0)}
        fill={datum.color}
        fontSize={14}
        className="capitalize"
        style={{ pointerEvents: "none" }}
      >
        {datum.emotion}
      </text>
    );
  }

  const image = getEmojiImage(datum?.emotion);
  if (!image) return null;

  return (
    <image
      href={image}
      x={x - 9}
      y={y - 9}
      width={18}
      height={18}
      style={{ pointerEvents: "none" }}
    />
  );
};

const LineGraphEmotionDropdown = ({
  selectedEmotions,
  onToggleEmotion,
  onToggleAllEmotions,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isAllSelected = selectedEmotions.length === barEmotionOrder.length;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative min-w-40" ref={dropdownRef}>
      <button
        id="line-graph-emotion-select"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="justify-between rounded p-1 border border-[#DDDDDD] w-full min-h-[34px] flex items-center cursor-pointer bg-white flex-wrap gap-2"
      >
        <div className="flex items-center gap-2 flex-1">
          {isAllSelected ? (
            <span className="px-2">All emotions</span>
          ) : (
            <>
              {selectedEmotions.slice(0, 1).map((emotion) => (
                <span
                  key={emotion}
                  className="bg-blue-100 text-[#2763D4] text-[14px] px-2 py-0.5 rounded-full capitalize"
                >
                  {emotion}
                </span>
              ))}
              {selectedEmotions.length > 1 && (
                <span className="text-[14px] text-400">
                  +{selectedEmotions.length - 1} more
                </span>
              )}
            </>
          )}
        </div>
        <svg
          className="w-3 h-3 text-gray-600 cursor-pointer mr-1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 10 6"
          fill="currentColor"
        >
          <path d="M0 0l5 6 5-6H0z" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-full bg-white border border-[#DDDDDD] rounded p-3 shadow-lg">
          <div className="text-[14px] mb-1 flex justify-between items-center">
            <span>Select Emotions</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-gray-600 hover:text-gray-800"
              aria-label="Close emotion dropdown"
            >
              <svg
                className="w-3 h-3 text-gray-600 cursor-pointer"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 10 6"
                fill="currentColor"
              >
                <path d="M0 0l5 6 5-6H0z" />
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-2 w-full text-left text-[14px] hover:bg-gray-50 cursor-pointer">
            <input
              id="line-graph-emotion-all"
              type="checkbox"
              checked={isAllSelected}
              onChange={onToggleAllEmotions}
              className="appearance-none w-4 h-4 border border-1 rounded checked:border-transparent"
              style={{
                backgroundColor: isAllSelected ? "#2763D4" : "transparent",
              }}
            />
            <label
              htmlFor="line-graph-emotion-all"
              className="flex-1 cursor-pointer text-base"
            >
              All emotions
            </label>
          </div>
          <div className="mt-1 grid grid-cols-1 gap-1">
            {barEmotionOrder.map((emotion) => {
              const selected = selectedEmotions.includes(emotion);
              const inputId = `line-graph-emotion-${emotion}`;

              return (
                <div
                  key={emotion}
                  className="flex items-center gap-2 w-full text-left px-2 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleEmotion(emotion)}
                    className="appearance-none w-4 h-4 border border-1 rounded checked:border-transparent"
                    style={{
                      backgroundColor: selected
                        ? emotionColors[emotion]
                        : "transparent",
                    }}
                  />
                  <label htmlFor={inputId} className="flex-1 cursor-pointer">
                    <span
                      className="text-[14px] capitalize"
                      style={{ color: emotionColors[emotion] }}
                    >
                      {emotion}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const EMPTY_LINE_GRAPH_DATA = {
  intervals: [],
  seriesByEmotion: {},
  tooltipRowsByInterval: [],
  hasData: false,
};

const hasPositiveData = (series = []) => series.some((point) => point.y > 0);

const buildYAxisScale = (seriesByEmotion, visibleEmotions) => {
  const percentages = visibleEmotions.flatMap((emotion) =>
    (seriesByEmotion[emotion] || [])
      .filter((point) => point.total > 0)
      .map((point) => point.y),
  );

  if (!percentages.length) {
    return { domain: [0, 100], ticks: [0, 25, 50, 75, 100] };
  }

  const minPercent = Math.min(...percentages);
  const maxPercent = Math.max(...percentages);

  let lower = 0;
  if (minPercent >= 25 && maxPercent - minPercent <= 20) {
    lower = Math.max(0, Math.floor(minPercent / 10) * 10);
  }

  const provisionalRange =
    Math.min(100, Math.max(10, Math.ceil(maxPercent / 10) * 10)) - lower;
  const step = provisionalRange <= 50 ? 10 : 25;

  lower = Math.floor(lower / step) * step;
  const upper = Math.min(
    100,
    Math.max(step, Math.ceil(maxPercent / step) * step),
  );

  const safeUpper = upper <= lower ? Math.min(100, lower + step) : upper;

  const ticks = [];
  for (let tick = lower; tick <= safeUpper; tick += step) {
    ticks.push(tick);
  }

  return { domain: [lower, safeUpper], ticks };
};

const LineGraph = ({
  lineGraphData,
  selectedEmotions = barEmotionOrder,
  updateState,
  studentEmailToNameMap = {},
}) => {
  const [activeTooltipKey, setActiveTooltipKey] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(true);
  const resolvedSelectedEmotions =
    selectedEmotions.length > 0 ? selectedEmotions : barEmotionOrder;
  const resolvedLineGraphData = lineGraphData || EMPTY_LINE_GRAPH_DATA;

  const visibleEmotions = useMemo(
    () =>
      resolvedSelectedEmotions.filter((emotion) =>
        hasPositiveData(resolvedLineGraphData.seriesByEmotion[emotion]),
      ),
    [resolvedLineGraphData, resolvedSelectedEmotions],
  );

  const yAxisScale = useMemo(
    () => buildYAxisScale(resolvedLineGraphData.seriesByEmotion, visibleEmotions),
    [resolvedLineGraphData, visibleEmotions],
  );

  const markerData = useMemo(() => {
    if (!resolvedLineGraphData.hasData) return [];

    const markers = visibleEmotions
      .map((emotion) => {
        const series = resolvedLineGraphData.seriesByEmotion[emotion] || [];
        const lastPoint = series[series.length - 1];
        return lastPoint ? { ...lastPoint, y: lastPoint.y, labelDy: 0 } : null;
      })
      .filter(Boolean);

    const sortedMarkers = [...markers].sort((a, b) => (a.y || 0) - (b.y || 0));
    const markerOffsets = new Map();

    for (let index = 0; index < sortedMarkers.length; ) {
      let clusterEnd = index;

      while (
        clusterEnd + 1 < sortedMarkers.length &&
        Math.abs(
          (sortedMarkers[clusterEnd + 1]?.y || 0) -
            (sortedMarkers[clusterEnd]?.y || 0),
        ) < EMOTION_LABEL_CLOSE_THRESHOLD
      ) {
        clusterEnd += 1;
      }

      const clusterSize = clusterEnd - index + 1;

      if (clusterSize > 1) {
        for (let clusterIndex = 0; clusterIndex < clusterSize; clusterIndex += 1) {
          const marker = sortedMarkers[index + clusterIndex];
          markerOffsets.set(
            marker.emotion,
            -clusterIndex * EMOTION_LABEL_VERTICAL_SPACING,
          );
        }
      }

      index = clusterEnd + 1;
    }

    return markers.map((marker) => ({
      ...marker,
      labelDy: markerOffsets.get(marker.emotion) || 0,
    }));
  }, [resolvedLineGraphData, visibleEmotions]);

  const tooltipData = useMemo(() => {
    if (!resolvedLineGraphData.hasData) return [];

    const tooltipY = (yAxisScale.domain[0] + yAxisScale.domain[1]) / 2;

    return resolvedLineGraphData.intervals.map(
      ({ x, key, tooltipLabel, tooltipDateLabel, year, start, end }, index) => ({
        x,
        key,
        intervalLabel: tooltipLabel,
        tooltipDateLabel,
        year,
        intervalStart: start,
        intervalEnd: end,
        y: tooltipY,
        isTooltipPoint: true,
        tooltipRows: resolvedLineGraphData.tooltipRowsByInterval[index],
      }),
    );
  }, [resolvedLineGraphData, yAxisScale]);

  const activeTooltipDatum = useMemo(
    () =>
      tooltipData.find(({ key }) => key === activeTooltipKey) ||
      tooltipData[tooltipData.length - 1] ||
      null,
    [activeTooltipKey, tooltipData],
  );

  const updateActiveTooltip = (points = []) => {
    const tooltipPoint = points.find((point) => point?.isTooltipPoint);
    if (!tooltipPoint) return;

    setIsTooltipVisible(true);
    setActiveTooltipKey((currentKey) =>
      currentKey === tooltipPoint.key ? currentKey : tooltipPoint.key,
    );
  };

  const toggleEmotion = (emotion) => {
    const isSelected = resolvedSelectedEmotions.includes(emotion);

    if (resolvedSelectedEmotions.length === barEmotionOrder.length) {
      updateState?.({ lineGraphSelectedEmotions: [emotion] });
      return;
    }

    if (isSelected && resolvedSelectedEmotions.length === 1) return;

    updateState?.({
      lineGraphSelectedEmotions: isSelected
        ? resolvedSelectedEmotions.filter(
            (selectedEmotion) => selectedEmotion !== emotion,
          )
        : barEmotionOrder.filter(
            (availableEmotion) =>
              resolvedSelectedEmotions.includes(availableEmotion) ||
              availableEmotion === emotion,
          ),
    });
  };

  const toggleAllEmotions = () => {
    updateState?.({ lineGraphSelectedEmotions: [...barEmotionOrder] });
  };

  const selectOnlyEmotion = (emotion) => {
    updateState?.({ lineGraphSelectedEmotions: [emotion] });
  };

  const { intervalTickValues, labeledIntervalTickValues, intervalLabels } =
    useMemo(() => {
      const tickValues = resolvedLineGraphData.intervals.map(({ x }) => x);
      const labeledTickValues = resolvedLineGraphData.intervals
        .filter((interval) => interval.label)
        .map(({ x }) => x);
      const labels = resolvedLineGraphData.intervals.reduce((acc, interval) => {
        acc[interval.x] = interval.label;
        return acc;
      }, {});

      return {
        intervalTickValues: tickValues,
        labeledIntervalTickValues: labeledTickValues,
        intervalLabels: labels,
      };
    }, [resolvedLineGraphData]);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex justify-between items-center mb-1 gap-3">
        <div
          htmlFor="line-graph-emotion-select"
          className="text-400 text-[18px] ml-6"
        >
          Discover emotion changes over time. Select emotion(s) to view:
        </div>
        <LineGraphEmotionDropdown
          selectedEmotions={resolvedSelectedEmotions}
          onToggleEmotion={toggleEmotion}
          onToggleAllEmotions={toggleAllEmotions}
        />
      </div>

      <div className="grid grid-cols-9 gap-2 mt-3 w-full px-8">
        {barEmotionOrder.map((emotion) => {
          const isSelected = resolvedSelectedEmotions.includes(emotion);
          const isDimmed = !isSelected;

          return (
            <button
              key={emotion}
              type="button"
              onClick={() => selectOnlyEmotion(emotion)}
              aria-label={`Show ${emotion} line`}
              aria-pressed={isSelected}
              className={`flex flex-col items-center gap-1 p-1 transition ${
                isDimmed ? "opacity-50" : "opacity-100"
              }`}
            >
              <img
                src={getEmojiImage(emotion)}
                alt={emotion}
                className="w-7 h-7"
              />
              <span
                className={`capitalize sm:text-xs md:text-sm 2xl:text-base text-[#595959]`}
              >
                {emotion}
              </span>
            </button>
          );
        })}
      </div>
      
      <div
        className="flex-1 min-h-0"
        onMouseEnter={() => setIsTooltipVisible(true)}
        onMouseLeave={() => setIsTooltipVisible(false)}
      >
        <VictoryChart
          width={CHART_WIDTH}
          height={CHART_HEIGHT}
          padding={{ top: 20, right: 64, bottom: 40, left: 58 }}
          domain={{
            x: [0, Math.max(resolvedLineGraphData.intervals.length - 1, 1)],
            y: yAxisScale.domain,
          }}
          containerComponent={
            <VictoryVoronoiContainer
              voronoiDimension="x"
              labels={() => null}
              onActivated={updateActiveTooltip}
            />
          }
          style={{ parent: { width: "100%", height: "100%" } }}
        >
          <VictoryAxis
            tickValues={intervalTickValues}
            tickFormat={(tick) => intervalLabels[tick] || ""}
            style={{
              axis: { stroke: "#555555" },
              ticks: { stroke: "transparent" },
              tickLabels: {
                fill: "#555555",
                fontSize: 14,
                fontWeight: 700,
                padding: 8,
              },
              grid: { stroke: "#EEEEEE", strokeWidth: 1 },
            }}
          />
          <VictoryAxis
            tickValues={labeledIntervalTickValues}
            tickFormat={() => ""}
            style={{
              axis: { stroke: "transparent" },
              ticks: { stroke: "#555555", size: 7, strokeWidth: 2 },
              tickLabels: { fill: "transparent" },
              grid: { stroke: "transparent" },
            }}
          />
          <VictoryAxis
            dependentAxis
            tickValues={yAxisScale.ticks}
            tickFormat={(tick) => (tick === 0 ? "" : `${tick}%`)}
            style={{
              axis: { stroke: "#555555" },
              ticks: { stroke: "transparent" },
              tickLabels: {
                fill: "#555555",
                fontSize: 14,
                fontWeight: 700,
                padding: 8,
              },
              grid: { stroke: "transparent" },
            }}
          />
          {resolvedLineGraphData.hasData &&
            visibleEmotions.map((emotion) => (
              <VictoryLine
                key={emotion}
                data={resolvedLineGraphData.seriesByEmotion[emotion]}
                interpolation="monotoneX"
                labels={() => null}
                style={{
                  data: {
                    stroke: emotionColors[emotion],
                    cursor: "pointer",
                  },
                }}
                events={[
                  {
                    target: "data",
                    eventHandlers: {
                      onClick: () => {
                        selectOnlyEmotion(emotion);
                        return [];
                      },
                    },
                  },
                ]}
              />
            ))}
          {resolvedLineGraphData.hasData &&
            visibleEmotions.map((emotion) => (
              <VictoryScatter
                key={`${emotion}-points`}
                data={resolvedLineGraphData.seriesByEmotion[emotion]}
                labels={() => null}
                size={2.6}
                style={{
                  data: {
                    fill: emotionColors[emotion],
                    stroke: emotionColors[emotion],
                    cursor: "pointer",
                  },
                }}
                events={[
                  {
                    target: "data",
                    eventHandlers: {
                      onClick: () => {
                        selectOnlyEmotion(emotion);
                        return [];
                      },
                    },
                  },
                ]}
              />
            ))}
          {resolvedLineGraphData.hasData && (
            <VictoryScatter
              name="tooltip-points"
              data={tooltipData}
              labels={() => null}
              size={24}
              style={{ data: { fill: "transparent", stroke: "transparent" } }}
            />
          )}
          {resolvedLineGraphData.hasData && isTooltipVisible && activeTooltipDatum && (
            <VictoryScatter
              name="active-tooltip"
              data={[activeTooltipDatum]}
              labels={() => " "}
              labelComponent={
                <LineGraphTooltip
                  selectedEmotions={resolvedSelectedEmotions}
                  studentEmailToNameMap={studentEmailToNameMap}
                />
              }
              size={0}
              style={{ data: { fill: "transparent", stroke: "transparent" } }}
            />
          )}
          {resolvedLineGraphData.hasData && (
            <VictoryScatter
              data={markerData}
              dataComponent={
                <EmotionMarker
                  showEmotionName={resolvedSelectedEmotions.length <= 3}
                />
              }
              labels={() => null}
              size={8}
            />
          )}
        </VictoryChart>
      </div>
    </div>
  );
};

export default LineGraph;
